import { Link } from "react-router-dom";
import { CircleX, Construction, Hourglass, ShieldX, TriangleAlert, Wrench } from "lucide-react";
import { Skeleton } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import { CopyableReference } from "@/components/ui/copyable-reference";
import { StatusDot, type Tone } from "@/components/ui/status";
import { cn } from "@/lib/utils";

export function LoadingState({ rows = 4 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading" className="flex flex-col gap-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} shimmer className="h-11 w-full rounded-md" />
      ))}
    </div>
  );
}

export function LoadingTable({ rows = 4, cols = 4, label = "Loading table" }: { rows?: number; cols?: number; label?: string }) {
  return (
    <div role="status" aria-label={label} className="overflow-hidden rounded-lg border border-line bg-surface">
      <div className="flex gap-6 border-b border-line bg-bg px-4 py-3">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} shimmer className="h-2.5 w-16 rounded" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="flex gap-6 border-b border-line px-4 py-3.5 last:border-b-0">
          {Array.from({ length: cols }).map((_, col) => (
            <Skeleton key={col} shimmer className="h-3 w-20 rounded" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Shared shell for every full-panel status card in this file (ErrorState,
 * PermissionDeniedState, NotAvailableState today): a bordered, centered card
 * with an optional icon (bare or circled), a bold title, and free-form body
 * content below it. Extracted so a design-token or spacing change is made
 * once, not independently in every state that happens to look like a card.
 * Not exported — compose a named state below instead of reaching for this
 * directly, so every card in the app stays a member of a named, intentional
 * state (empty/error/denied/not-available/...), never an ad hoc one.
 */
function StateCard({
  role = "status",
  tone,
  padding = "py-12",
  icon,
  iconCircle = false,
  iconCircleSize = "size-16",
  title,
  children,
  className,
}: {
  role?: "status" | "alert";
  tone: "danger" | "neutral-surface" | "neutral-dashed";
  padding?: string;
  icon?: React.ReactNode;
  iconCircle?: boolean;
  iconCircleSize?: string;
  title: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const toneClasses =
    tone === "danger"
      ? "border-danger-border bg-danger-soft"
      : tone === "neutral-surface"
        ? "border-line bg-surface"
        : "border-dashed border-line bg-bg";
  // `px-4` on a phone, the 24px design scale from `sm`: inside a page that
  // already pads, the desktop pair left too little room for a full reason code
  // or a long URL at 375px.
  return (
    <div
      role={role}
      className={cn("flex flex-col items-center gap-3 rounded-lg border text-center px-4 sm:px-6", toneClasses, padding, className)}
    >
      {icon &&
        (iconCircle ? (
          <div className={cn("grid place-items-center rounded-full border border-line bg-bg text-2xl", iconCircleSize)}>
            {icon}
          </div>
        ) : (
          icon
        ))}
      <div className="text-base font-bold text-ink">{title}</div>
      {children}
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  description,
  details,
  retryLabel = "Try again",
  onRetry,
}: {
  title?: string;
  description?: string;
  details?: string;
  retryLabel?: string;
  onRetry?: () => void;
}) {
  return (
    <StateCard role="alert" tone="danger" icon={<CircleX aria-hidden className="size-10 text-danger" />} title={title}>
      {description && <p className="max-w-[420px] text-sm leading-relaxed text-muted">{description}</p>}
      {details && <p className="max-w-[420px] font-mono text-xs text-danger">{details}</p>}
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </StateCard>
  );
}

/**
 * Honest "not built yet" state for a page/panel whose backend does not exist
 * in this environment (G2 `defer` decision, page-delivery-pipeline.md).
 *
 * Deliberately distinct from EmptyState (a real API returned zero rows),
 * ErrorState (a real call failed) and MaintenanceState (a real capability is
 * temporarily down). This state means: nothing has ever been wired here, and
 * nothing on this screen should be read as live data. Never pair this with a
 * static count, badge, or any other value that implies a data source exists.
 */
export function NotAvailableState({
  title = "Not available in this environment yet",
  description,
  reason,
  className,
}: {
  title?: string;
  description?: string;
  reason?: string;
  className?: string;
}) {
  return (
    <StateCard
      tone="neutral-dashed"
      padding="py-14"
      icon={<Construction aria-hidden className="size-6 text-muted" />}
      iconCircle
      iconCircleSize="size-12"
      title={title}
      className={className}
    >
      {description && <p className="max-w-[460px] text-sm leading-relaxed text-muted">{description}</p>}
      {reason && <p className="max-w-[460px] text-xs text-muted">{reason}</p>}
    </StateCard>
  );
}

export function MaintenanceState({
  title = "Scheduled maintenance",
  description,
  endsAt,
  rows,
  actions,
  className,
}: {
  title?: string;
  description?: string;
  endsAt?: string;
  rows?: { k: string; v: React.ReactNode }[];
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex flex-col gap-4 rounded-lg border border-warning-border bg-warning-soft px-4 py-8 sm:px-6",
        className
      )}
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <Wrench aria-hidden className="size-10 text-warning" />
        <div className="text-base font-bold text-ink">{title}</div>
        {description && <p className="max-w-[520px] text-sm leading-relaxed text-muted">{description}</p>}
        {endsAt && <p className="text-xs font-semibold text-warning">Expected to resume {endsAt}</p>}
      </div>
      {rows && rows.length > 0 && (
        <div className="overflow-hidden rounded-md border border-warning-border bg-surface">
          {rows.map((row, i) => (
            <div
              key={`${row.k}-${i}`}
              className={cn(
                // Key above value on a phone; the designer's 160px + 1fr row
                // from `sm`, where there is room for it. A fixed 160px column
                // at 375px leaves the value ~120px, which a URL or a timestamp
                // cannot use.
                "grid grid-cols-1 gap-1 px-4 py-2.5 text-sm sm:grid-cols-[160px_1fr] sm:gap-3",
                i < rows.length - 1 && "border-b border-line"
              )}
            >
              <span className="text-muted">{row.k}</span>
              <span className="min-w-0 break-words font-semibold text-ink">{row.v}</span>
            </div>
          ))}
        </div>
      )}
      {actions && <div className="flex flex-wrap justify-center gap-3">{actions}</div>}
    </div>
  );
}

export function PermissionDeniedState({
  title = "Access required",
  description,
  icon,
  details,
  action,
  footnote,
}: {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  details?: { k: string; v: string }[];
  action?: React.ReactNode;
  footnote?: React.ReactNode;
}) {
  return (
    <StateCard
      role="alert"
      tone="neutral-surface"
      icon={icon ?? <ShieldX aria-hidden className="size-8 text-danger" />}
      iconCircle
      title={title}
    >
      {description && <p className="max-w-[460px] text-sm leading-relaxed text-muted">{description}</p>}
      {details && details.length > 0 && (
        <div className="w-full max-w-[480px] rounded-md border border-info-border bg-info-soft p-4 text-left">
          <div className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-info">Access details</div>
          <div className="flex flex-col gap-2">
            {details.map((d) => (
              // Key above value on a phone; the fixed 120px label column and
              // its value side by side from `sm`.
              <div key={d.k} className="flex flex-col gap-0.5 text-sm sm:flex-row sm:gap-3">
                <span className="text-muted sm:w-[120px] sm:shrink-0">{d.k}</span>
                <span className="min-w-0 break-words font-semibold text-ink">{d.v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {action && <div className="mt-1">{action}</div>}
      {footnote && <div className="text-xs text-muted">{footnote}</div>}
    </StateCard>
  );
}

export function StaleState({
  title = "Data may be stale",
  description,
  lastSyncedAt,
  action,
  className,
}: {
  title?: string;
  description?: string;
  lastSyncedAt?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div role="alert" className={cn("mb-4 flex flex-col gap-3 rounded-md border border-warning-border bg-warning-soft px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between", className)}>
      <div className="flex items-start gap-2.5">
        <Hourglass aria-hidden className="mt-0.5 size-4 shrink-0 text-warning" />
        <div className="min-w-0">
          <div className="text-xs font-bold text-warning">{title}</div>
          {description && <p className="mt-0.5 text-xs text-warning">{description}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {lastSyncedAt && <span className="whitespace-nowrap text-xs text-warning">{lastSyncedAt}</span>}
        {action}
      </div>
    </div>
  );
}

export function OutcomeUnknownState({
  title = "Outcome unresolved",
  description,
  traceId,
  actions,
  className,
}: {
  title?: string;
  description: string;
  traceId?: string;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <section role="status" className={cn("rounded-lg border border-line bg-console-bg p-5", className)}>
      <div className="flex items-center justify-between gap-3">
        <span className="min-w-0 break-all font-mono text-xs font-semibold uppercase tracking-wider text-console-warning">{title}</span>
        <TriangleAlert aria-hidden className="size-4 shrink-0 text-console-warning" />
      </div>
      <p className="mt-2 font-mono text-[13px] leading-relaxed text-console-text">{description}</p>
      {traceId && <p className="mt-3 break-all font-mono text-[11px] text-console-muted">Trace ID {traceId}</p>}
      {actions && <div className="mt-4 flex flex-wrap gap-2">{actions}</div>}
    </section>
  );
}

/**
 * A reason code (`HTTP_403`, `AUTH_POLICY_DENIED`, `UNKNOWN_CLIENT_ERROR`, …) is
 * SCREAMING_SNAKE machine text, never a human incident label. UX audit D12:
 * user-facing error surfaces must not print a machine code; it stays available
 * to support through the copied details block / support reference instead. A
 * human identifier such as `INC-4417` or "Incident INC-2048 · started 08:42
 * UTC" still renders.
 */
const MACHINE_REASON_CODE = /^[A-Z][A-Z0-9_]{3,}$/;

/**
 * Full-page error specimen (SYS-04). Shared by the 503/404 specimens, the global
 * `not-found.tsx` and the global `error.tsx` boundary.
 *
 * `supportReference` is the server-minted reference (D636) for the request that
 * failed, rendered through the design-system CopyableReference so a person on a
 * 404/5xx has something specific to hand support. It is omitted entirely when
 * absent — a client-side route miss mints nothing, and a placeholder would send
 * support after a request that was never recorded.
 *
 * `incident` is an incident *label* for humans; a caller that passes a machine
 * reason code (the onboarding invite surface did) gets nothing printed — see
 * MACHINE_REASON_CODE above.
 */
export function FullPageError({
  code,
  title,
  description,
  incident,
  supportReference,
  actions,
  statuses,
  codeTone = "primary",
  homeHref,
  homeLabel,
}: {
  code: string;
  title: string;
  description: string;
  incident?: string;
  supportReference?: string | null;
  actions?: React.ReactNode;
  statuses?: { label: string; tone: Tone }[];
  codeTone?: "primary" | "warning" | "danger";
  homeHref?: string;
  homeLabel?: string;
}) {
  return (
    <div className="flex flex-col items-center rounded-lg border border-line bg-surface px-4 py-10 text-center sm:px-6 sm:py-14">
      <div
        className={cn(
          "font-mono text-[64px] font-bold leading-none",
          codeTone === "primary" && "text-primary",
          codeTone === "warning" && "text-warning",
          codeTone === "danger" && "text-danger"
        )}
      >
        {code}
      </div>
      <div className="mt-3 text-xl font-extrabold text-ink">{title}</div>
      <p className="mt-2 max-w-[460px] text-sm leading-relaxed text-muted">{description}</p>
      {incident && !MACHINE_REASON_CODE.test(incident.trim()) && (
        <p className="mt-2 min-w-0 max-w-full break-all font-mono text-xs text-muted">{incident}</p>
      )}
      {supportReference && (
        <div className="mt-4 flex flex-wrap justify-center">
          <CopyableReference reference={supportReference} />
        </div>
      )}
      {(actions || homeHref) && (
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {homeHref && (
            <Link
              to={homeHref}
              className="inline-flex min-h-[36px] items-center justify-center rounded-sm bg-primary px-3.5 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-hover"
            >
              {homeLabel ?? "Go home"}
            </Link>
          )}
          {actions}
        </div>
      )}
      {statuses && statuses.length > 0 && (
        <div className="mt-6 flex flex-wrap justify-center gap-4">
          {statuses.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1.5 text-xs text-muted">
              <StatusDot tone={s.tone} /> {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
